/* eslint-disable no-param-reassign */
/* eslint-disable no-shadow */

// import * as ModelEvent from "@node-elion/syncron";
import ServiceSubscribeOptionsBase from "../../types/ServiceSubscribeOptionsBase";
import { SortingOrder } from "../../types/SortingOrder";
import {
	Base,
	ExecutorRateClassifier,
	ExecutorRateCommissionPlan,
	Language,
} from "..";

// import SubscriptionBase from "../../types/Subscription";
// import SubscriptionPool from "../../redux/services/SubscriptionPool";

const weekdayToNumber = {
	monday: 0,
	tuesday: 1,
	wednesday: 2,
	thursday: 3,
	friday: 4,
	saturday: 5,
	sunday: 6,
};

const conditionOperatorMaps = {
	fromResponse: {
		greater: "greater",
		less: "less",
		equal: "equal",
		greaterEqual: "greaterOrEqual",
		lessEqual: "lessOrEqual",
	},
	toRequest: {
		greater: "greater",
		less: "less",
		equal: "equal",
		greaterOrEqual: "greaterEqual",
		lessOrEqual: "lessEqual",
	},
} as const;

const filterItems = [
	"terminalToExecutor",
	"externalTransfer",
	"cardPaymentToExecutor",
];

class ExecutorRateDiscountPlan extends Base {
	static fromResponse(data: any): ExecutorRateDiscountPlan.Model {
		return {
			id: data.id,

			// classifier: data.classifier
			// 	? ExecutorRateClassifier.fromResponse(data.classifier)
			// 	: undefined,

			commissionPlan: data.commissionPlan
				? ExecutorRateCommissionPlan.fromResponse(data.commissionPlan)
				: undefined,

			name: data.name,

			threshold: data.threshold,

			startDate: data.startDate,

			ignoreOldOrders: data?.ignoreOldOrders ?? false,

			weekdays: {
				monday: data.weekdays?.includes(0) ?? false,
				tuesday: data.weekdays?.includes(1) ?? false,
				wednesday: data.weekdays?.includes(2) ?? false,
				thursday: data.weekdays?.includes(3) ?? false,
				friday: data.weekdays?.includes(4) ?? false,
				saturday: data.weekdays?.includes(5) ?? false,
				sunday: data.weekdays?.includes(6) ?? false,
			},

			period: {
				type: data.period,
				value: data.periodValue,
			},

			timeRange: {
				active: data.configuration?.timeRange?.active ?? false,
				from: data.configuration?.timeRange?.from ?? 0,
				to: data.configuration?.timeRange?.to ?? 0,
			},

			rate: {
				considerTemporaryDiscountedOrders:
					data.configuration?.rate
						?.considerTemporaryDiscountedOrders ?? false,
				considerTemporaryCommissionOrders:
					data.configuration?.rate
						?.considerTemporaryCommissionOrders ?? false,

				minCommission: data.configuration?.rate?.minCommission
					? {
							active: data.configuration.rate.minCommission
								.active,
							amount: data.configuration.rate.minCommission
								.amount,
					  }
					: undefined,

				maxCommission: data.configuration?.rate?.maxCommission
					? {
							active: data.configuration.rate.maxCommission
								.active,
							amount: data.configuration.rate.maxCommission
								.amount,
					  }
					: undefined,

				commissions: data.configuration?.rate?.commissionSortingOrder
					?.filter(
						(commission) => !filterItems.includes(commission.type),
					)
					?.map(
						(
							commissionType: ExecutorRateDiscountPlan.Model.Rate.Commission.Type,
						) => {
							const commission =
								data.configuration.rate.commissions[
									commissionType
								];

							return {
								type: commissionType,

								percent: commission.percent,

								amount: commission.amount,

								threshold: {
									active: commission.threshold.active,

									comparison: {
										direction:
											conditionOperatorMaps.fromResponse[
												commission.threshold.comparison
													.direction
											],
										value: commission.threshold.comparison
											.value,
									},
									fee: {
										percent:
											commission.threshold.fee.percent,
										amount: commission.threshold.fee.amount,
									},
								},
							};
						},
					),
			},

			action: data.action,

			updatedAt: data.updatedAt,
			createdAt: data.createdAt,
			deletedAt: data.deletedAt,
		};
	}

	static toRequest(
		model:
			| ExecutorRateDiscountPlan.Model.New
			| ExecutorRateDiscountPlan.Model.Modified,
	): any {
		const startDate = model?.startDate
			? new Date(model.startDate).getTime()
			: model?.startDate;

		return {
			startDate,
			name: model.name,
			threshold: model.threshold || 1,
			ignoreOldOrders: model?.ignoreOldOrders ?? false,
			period: model.period?.type,
			periodValue: model.period?.value,
			weekdays: Object.entries(model.weekdays ?? {})
				.filter(([, value]) => value)
				.map(([key]) => weekdayToNumber[key] as number),
			commissionPlanId: model.commissionPlanId,
			// classifierId: model.classifierId,
			configuration: {
				timeRange: model.timeRange
					? {
							active: model.timeRange.active,
							from: model.timeRange.from,
							to: model.timeRange.to,
					  }
					: undefined,

				rate: model.rate
					? {
							considerTemporaryDiscountedOrders:
								model.rate.considerTemporaryCommissionOrders,
							considerTemporaryCommissionOrders:
								model.rate.considerTemporaryCommissionOrders,

							commissions: model.rate.commissions?.reduce(
								(acc, commission) => ({
									...acc,
									[commission.type]: {
										percent: commission.percent,
										amount: commission.amount,
										threshold: {
											active: commission.threshold.active,
											comparison: {
												direction:
													conditionOperatorMaps
														.toRequest[
														commission.threshold
															.comparison
															.direction
													],
												value: commission.threshold
													.comparison.value,
											},
											fee: {
												percent:
													commission.threshold.fee
														.percent,
												amount: commission.threshold.fee
													.amount,
											},
										},
									},
								}),
								{},
							),

							commissionSortingOrder:
								model.rate.commissions
									?.filter(
										(commission) =>
											!filterItems.includes(
												commission.type,
											),
									)
									?.map((commission) => commission.type) ??
								[],

							minCommission: {
								active:
									model.rate.minCommission?.active ?? false,
								amount: model.rate.minCommission?.amount ?? 0,
							},

							maxCommission: {
								active:
									model.rate.maxCommission?.active ?? false,
								amount: model.rate.maxCommission?.amount ?? 0,
							},
					  }
					: undefined,
			},
		};
	}
}

namespace ExecutorRateDiscountPlan {
	export interface Model {
		id: number;

		classifier?: ExecutorRateClassifier.Model;
		commissionPlan?: ExecutorRateCommissionPlan.Model;

		name: Record<Language, string>;
		threshold: number;
		startDate: Date | string;
		weekdays: Model.Weekdays;
		ignoreOldOrders?: boolean;
		period: Model.Period;
		timeRange: Model.TimeRange;
		rate: Model.Rate;
		action: Model.Action;

		updatedAt: string;
		createdAt: string;
		deletedAt: string | null;
	}

	export interface SubscribeOptions
		extends Omit<
			ServiceSubscribeOptionsBase<ExecutorRateDiscountPlan.Model>,
			"order"
		> {
		order?: Partial<Record<keyof Model, SortingOrder>>;
	}

	export namespace Model {
		export enum Action {
			AfterOrderClosed = "after_order_closed",
			AfterOrderAssigned = "after_order_assigned",
			AfterPeriodEnd = "after_end_period",
		}

		export interface Weekdays {
			monday: boolean;
			tuesday: boolean;
			wednesday: boolean;
			thursday: boolean;
			friday: boolean;
			saturday: boolean;
			sunday: boolean;
		}

		export interface Period {
			type: Period.Type;
			value: number;
		}

		export interface TimeRange {
			active: boolean;

			from: number;
			to: number;
		}

		export interface Rate {
			considerTemporaryDiscountedOrders: boolean;
			considerTemporaryCommissionOrders: boolean;

			minCommission?: Rate.MinMaxCommission;
			maxCommission?: Rate.MinMaxCommission;

			commissions?: Rate.Commission[];
		}

		export interface New {
			// classifierId: number;
			commissionPlanId: number;

			name: Record<Language, string>;
			threshold: number;
			ignoreOldOrders?: boolean;
			startDate: Date | string;
			weekdays: Model.Weekdays;
			period: Model.Period;
			timeRange?: Model.TimeRange;
			rate: Model.Rate;
			action: Model.Action;
		}
		export type Modified = Partial<New> & { id: number };

		export namespace Period {
			export enum Type {
				Daily = "daily",
				Weekly = "weekly",
				Monthly = "monthly",
			}
		}

		export namespace Rate {
			export interface Commission {
				type: Commission.Type;

				percent: number;

				amount: number;

				threshold: Commission.Threshold;
			}

			export interface MinMaxCommission {
				active: boolean;

				amount: number;
			}

			export namespace Commission {
				export enum Type {
					CashOrders = "cashOrders",
					NonCashOrders = "nonCashOrders",
					SuburbanOrders = "suburbanOrders",
					OnlineOrders = "onlineOrders",
					BonusOrders = "bonusOrders",
					ExecutorOrder = "executorOrder",
					CardPaymentToTaxiService = "cardPaymentToTaxiService",
					// CardPaymentToExecutor = "cardPaymentToExecutor",
					TerminalToTaxiServiceService = "terminalToTaxiService",
					// TerminalToExecutor = "terminalToExecutor",
					LocalTransfer = "localTransfer",
					GlobalTransfer = "globalTransfer",
					// ExternalTransfer = "externalTransfer",
				}

				export interface Threshold {
					active: boolean;

					comparison: Threshold.Commission;
					fee: Threshold.Fee;
				}

				export namespace Threshold {
					export interface Commission {
						direction: Commission.Direction;
						value: number;
					}

					export interface Fee {
						percent: number;
						amount: number;
					}

					export namespace Commission {
						export enum Direction {
							Less = "less",
							Greater = "greater",
							Equal = "equal",
							LessOrEqual = "lessOrEqual",
							GreaterOrEqual = "greaterOrEqual",
						}
					}
				}
			}
		}
	}
}

export default ExecutorRateDiscountPlan;
